import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ApiError, NetworkError } from "../api/client";
import { getMemberDetail, getMemberHistory, type MemberHistory, type MemberProfile } from "../api/members";
import { useAuth } from "../auth/AuthContext";
import { MemberIdentityHeader } from "../components/members/MemberIdentityHeader";
import { MemberProfileSections } from "../components/members/MemberProfileSections";
import { MemberFinancialPanel } from "../components/MemberFinancialPanel";
import { StatusBanner } from "../components/StatusBanner";
import { useOnlineStatus } from "../hooks/useOnlineStatus";

type DetailView =
  | { type: "loading" }
  | { type: "ready"; profile: MemberProfile; history: MemberHistory }
  | { type: "offline" }
  | { type: "forbidden" }
  | { type: "not-found" }
  | { type: "error" };

// The page branches on independent fetch states and capability-gated actions; authorization remains
// delegated to CapabilityRoute and the backend contracts.
// eslint-disable-next-line complexity
export function MemberDetailPage() {
  const { memberId } = useParams();
  const { state, expireSession } = useAuth();
  const isOnline = useOnlineStatus();
  const accessToken = state.status === "authenticated" ? state.accessToken : null;
  const [view, setView] = useState<DetailView>({ type: "loading" });

  useEffect(() => {
    if (!accessToken || !memberId) return;
    let cancelled = false;

    if (!isOnline) {
      setView({ type: "offline" });
      return;
    }

    setView({ type: "loading" });
    void Promise.all([getMemberDetail(accessToken, memberId), getMemberHistory(accessToken, memberId)])
      .then(([profile, history]) => {
        if (!cancelled) setView({ type: "ready", profile, history });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          expireSession();
          return;
        }
        if (error instanceof ApiError && error.status === 403) {
          setView({ type: "forbidden" });
          return;
        }
        if (error instanceof ApiError && error.status === 404) {
          setView({ type: "not-found" });
          return;
        }
        if (error instanceof NetworkError) {
          setView({ type: "offline" });
          return;
        }
        setView({ type: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, expireSession, isOnline, memberId]);

  if (state.status !== "authenticated") return null;
  if (!memberId) return <main className="member-detail-page"><StatusBanner tone="error">Identificador de associado inválido.</StatusBanner></main>;

  return (
    <main className="member-detail-page">
      <Link className="member-detail-page__back" to="/associados">Voltar para associados</Link>
      {view.type === "loading" && <p className="members-state" role="status" aria-live="polite">Carregando ficha do associado…</p>}
      {view.type === "offline" && <StatusBanner tone="warning">Sem conexão. Verifique a internet e tente novamente.</StatusBanner>}
      {view.type === "forbidden" && <StatusBanner tone="error">Seu acesso não possui permissão para consultar esta ficha.</StatusBanner>}
      {view.type === "not-found" && <StatusBanner tone="info">Não encontramos este associado.</StatusBanner>}
      {view.type === "error" && <StatusBanner tone="error">Não foi possível carregar esta ficha. Tente novamente.</StatusBanner>}
      {view.type === "ready" && (
        <>
          <header className="member-detail-page__intro">
            <p className="members-page__eyebrow">Ficha institucional</p>
            <MemberIdentityHeader
              fullName={view.profile.full_name}
              preferredName={view.profile.preferred_name}
              username={view.profile.username}
              status={view.profile.status}
              electedRoles={view.profile.elected_roles}
            />
            <div className="member-detail-actions" aria-label="Ações disponíveis">
              {(state.sessionContext?.capabilities.write_members || state.memberId === memberId) && <Link className="btn btn--secondary" to={`/associados/${memberId}/editar`}>{state.memberId === memberId && !state.sessionContext?.capabilities.write_members ? "Atualizar contato" : "Corrigir cadastro"}</Link>}
              {state.sessionContext?.capabilities.write_departments && <Link className="btn btn--secondary" to={`/associados/${memberId}/departamentos`}>Novo Departamento</Link>}
              {state.sessionContext?.capabilities.reset_password && <Link className="btn btn--secondary" to={`/associados/${memberId}/senha`}>Redefinir senha</Link>}
              {state.sessionContext?.capabilities.request_mfa_recovery && <Link className="btn btn--secondary" to={`/mfa/recuperacao/solicitar?member_id=${encodeURIComponent(memberId)}`}>Recuperar MFA</Link>}
              {state.sessionContext?.capabilities.manage_access && <Link className="btn btn--secondary" to={`/acessos?member_id=${memberId}`}>Gerir acesso</Link>}
            </div>
          </header>
          <MemberProfileSections profile={view.profile} history={view.history} memberId={memberId} canWriteDepartments={Boolean(state.sessionContext?.capabilities.write_departments)} />
          {state.sessionContext?.capabilities.read_payments && accessToken && (
            <MemberFinancialPanel
              accessToken={accessToken}
              memberId={memberId}
              onSessionExpired={expireSession}
            />
          )}
        </>
      )}
    </main>
  );
}
